// Single review card with search-term highlighting.
import { CircleAlert, Reply, ThumbsDown, ThumbsUp } from "lucide-react";
import { useState } from "react";
import { SENTIMENT_BY_KEY, URGENCY_LABELS } from "./constants";
import { Stars } from "./ui";
import { formatDate } from "./utils";

export const highlight = (text, query) => {
  const terms = (query || "")
    .trim()
    .split(/\s+/)
    .filter((t) => t.length > 1);
  if (!terms.length || !text) return text;
  const escaped = terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const parts = text.split(new RegExp(`(${escaped.join("|")})`, "gi"));
  return parts.map((part, i) =>
    i % 2 === 1 ? <mark key={i}>{part}</mark> : part,
  );
};

const ReviewCard = ({ item, onFilter, query, compact = false }) => {
  const { review, analysis } = item;
  const [expanded, setExpanded] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const sentiment = SENTIMENT_BY_KEY[analysis.sentiment];
  const text = review.text || "";
  const long = text.length > (compact ? 220 : 420);

  return (
    <article className={`gr-review gr-review-${analysis.sentiment}`}>
      <header className="gr-review-head">
        <div className="gr-avatar" aria-hidden="true">
          {(review.author || "?").trim().charAt(0).toUpperCase()}
        </div>
        <div className="gr-review-who">
          <strong>{review.author || "Anonimowy użytkownik"}</strong>
          <span>
            <time
              dateTime={review.exact_date}
              title={
                item.date_estimated
                  ? `Data szacowana na podstawie „${review.date}”`
                  : undefined
              }
            >
              {item.date_estimated && "≈ "}
              {formatDate(review.exact_date)}
            </time>
            {review.likes ? (
              <>
                {" "}
                · <ThumbsUp size={12} aria-hidden="true" /> {review.likes}
              </>
            ) : null}
          </span>
        </div>
        <div className="gr-review-score">
          <Stars value={review.rating} size="sm" />
          {sentiment && (
            <span className={`gr-badge gr-badge-${analysis.sentiment}`}>
              {sentiment.single}
            </span>
          )}
        </div>
      </header>

      {text ? (
        <p
          className={`gr-review-text ${long && !expanded ? "is-clamped" : ""}`}
        >
          {highlight(text, query)}
        </p>
      ) : (
        <p className="gr-review-text gr-muted">
          Opinia bez treści – tylko ocena gwiazdkowa.
        </p>
      )}
      {long && (
        <button
          type="button"
          className="gr-link-btn"
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded ? "Zwiń" : "Czytaj całość"}
        </button>
      )}

      {!compact &&
        (analysis.strengths.length > 0 || analysis.weaknesses.length > 0) && (
          <div className="gr-review-chips">
            {analysis.strengths.map((s) => (
              <button
                type="button"
                key={`s-${s}`}
                className="gr-chip gr-chip-positive"
                onClick={() => onFilter({ strength: s })}
                title="Pokaż opinie z tą mocną stroną"
              >
                <ThumbsUp size={12} aria-hidden="true" />
                {s}
              </button>
            ))}
            {analysis.weaknesses.map((w) => (
              <button
                type="button"
                key={`w-${w}`}
                className="gr-chip gr-chip-negative"
                onClick={() => onFilter({ weakness: w })}
                title="Pokaż opinie z tą słabą stroną"
              >
                <ThumbsDown size={12} aria-hidden="true" />
                {w}
              </button>
            ))}
          </div>
        )}

      <footer className="gr-review-meta">
        <button
          type="button"
          className="gr-tag"
          onClick={() => onFilter({ department: analysis.department })}
        >
          {item.department_label}
        </button>
        {item.topic_key !== "brak_tresci" && (
          <button
            type="button"
            className="gr-tag"
            onClick={() => onFilter({ topic: item.topic_key })}
          >
            {item.topic_label}
          </button>
        )}
        {analysis.employee_names.map((name) => (
          <button
            type="button"
            key={name}
            className="gr-tag gr-tag-person"
            onClick={() => onFilter({ employee: name })}
          >
            {name}
          </button>
        ))}
        {analysis.urgency_level !== "niski" && (
          <span className={`gr-tag gr-tag-urgency-${analysis.urgency_level}`}>
            <CircleAlert size={12} aria-hidden="true" /> Pilność:{" "}
            {URGENCY_LABELS[analysis.urgency_level]}
          </span>
        )}
        {analysis.issue_resolved === false && (
          <span className="gr-tag gr-tag-warn">Problem nierozwiązany</span>
        )}
      </footer>

      {review.owner_reply && (
        <div className="gr-reply">
          <button
            type="button"
            className="gr-link-btn"
            onClick={() => setReplyOpen((o) => !o)}
            aria-expanded={replyOpen}
          >
            <Reply size={14} aria-hidden="true" />{" "}
            {replyOpen ? "Ukryj odpowiedź firmy" : "Odpowiedź firmy"}
          </button>
          {replyOpen && <p>{review.owner_reply}</p>}
        </div>
      )}
    </article>
  );
};

export default ReviewCard;
